import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChipBlock } from 'components'
import {
  SectionLabel,
  DateField,
  TextBlock,
  FieldBlock,
  ContentBlock,
  Checkbox,
  useToast,
} from '@politechdev/blocks-design-system'
import { useCurrent } from 'contexts'
import { useDeliveryActions, useDeliveryState } from './DeliveryContext'
import {
  getEligibleFormIds,
  buildCountyOptions,
  usesScanLookupCodes,
} from './utils'

const DeliveryFilters = () => {
  const { t } = useTranslation()

  const { setToast } = useToast()

  const { currentTurfs, refreshCurrentTurfs } = useCurrent()

  const {
    currentDelivery,
    pendingUpdates,
    packets,
    isDeliveryLoading,
    packetsLoading,
  } = useDeliveryState()

  const eligibleFormIds = getEligibleFormIds({
    delivery: currentDelivery,
    pendingUpdates,
    packets,
    searchTerm: '',
    currentTurfs,
    options: {
      county: false,
    },
  })

  const countyOptions = buildCountyOptions({ eligibleFormIds, packets })

  const pendingChanges = pendingUpdates.reduce(
    (combinedChanges, currentChange) => ({
      ...combinedChanges,
      ...currentChange.change,
    }),
    {}
  )

  const counties =
    pendingChanges.form_filter_counties ?? currentDelivery.form_filter_counties

  const noCounty =
    pendingChanges.form_filters_no_county ??
    !!currentDelivery.form_filters_no_county

  const startDate =
    pendingChanges.form_filter_registration_date_start ??
    currentDelivery.form_filter_registration_date_start

  const endDate =
    pendingChanges.form_filter_registration_date_end ??
    currentDelivery.form_filter_registration_date_end

  const { updateDelivery, applyOptimisticDeliveryUpdate } = useDeliveryActions()

  const doUpdateDelivery = async (id, attrs) => {
    const rollback = applyOptimisticDeliveryUpdate(attrs)
    const res = await updateDelivery(id, attrs)
    if (!res.delivery) {
      setToast({
        message: t('A filter update has failed, please verify filters'),
        variant: 'error',
      })
    }
    rollback()
  }

  const changeDateRange = (newStartDateTime, newEndDateTime) => {
    const newStartDate = newStartDateTime
      ? newStartDateTime.slice(0, newStartDateTime.indexOf('T'))
      : null

    const newEndDate = newEndDateTime
      ? newEndDateTime.slice(0, newEndDateTime.indexOf('T'))
      : null

    doUpdateDelivery(currentDelivery.id, {
      form_filter_registration_date_start: newStartDate,
      form_filter_registration_date_end: newEndDate,
    })
  }

  const addCounty = selectedCounty => {
    const newCounties = [...counties, selectedCounty]

    doUpdateDelivery(currentDelivery.id, {
      form_filter_counties: newCounties,
    })
  }

  const addAllCounties = () => {
    const allCounties = countyOptions
      .filter(county => !!county)
      .map(county => county.id)

    doUpdateDelivery(currentDelivery.id, {
      form_filter_counties: allCounties,
    })
  }

  const clearCounties = () => {
    doUpdateDelivery(currentDelivery.id, {
      form_filter_counties: [],
      form_filters_no_county: false,
    })
  }

  const removeCounty = selectedCounty => {
    const newCounties = counties.filter(county => county !== selectedCounty)
    doUpdateDelivery(currentDelivery.id, {
      form_filter_counties: newCounties,
      form_filters_no_county: newCounties.length ? noCounty : false,
    })
  }

  const setNoCounties = value => {
    doUpdateDelivery(currentDelivery.id, {
      form_filters_no_county: value,
    })
  }

  useEffect(() => {
    refreshCurrentTurfs()
  }, [])

  return (
    <div className="delivery__single__filters">
      <ChipBlock
        label={t('Counties')}
        loading={isDeliveryLoading || packetsLoading}
        choices={countyOptions.filter(
          county => !counties.includes(county.id) && !!county
        )}
        selectedChips={counties}
        addChip={addCounty}
        removeChip={removeCounty}
        addAll={addAllCounties}
        removeAll={clearCounties}
        autoBlur={false}
        closeOnSelect={false}
      />
      <TextBlock className="no-county-checkbox">
        <Checkbox
          id="form_filters_no_county"
          name="Include forms without a county"
          label={t('Include forms without a county')}
          checked={noCounty}
          onChange={setNoCounties}
        />
      </TextBlock>
      {!usesScanLookupCodes(currentDelivery) && (
        <>
          <ContentBlock>
            <SectionLabel>{t('Filters')}</SectionLabel>
          </ContentBlock>
          <FieldBlock variant="medium">
            <DateField
              label={t('Shift start')}
              type="range"
              startDate={startDate}
              endDate={endDate}
              onChange={changeDateRange}
              onClear={changeDateRange}
              clearable
            />
          </FieldBlock>
        </>
      )}
    </div>
  )
}

export default DeliveryFilters
